import React from 'react';
import {
  ArrowRight,
  Shield,
  Clock,
  CreditCard,
  HelpCircle,
  Layers,
  RefreshCw,
  Ban,
  TrendingUp,
  FileText,
  ClipboardList,
  Sparkles,
  CheckCircle2,
  Building2,
  Rocket,
  Users,
  Briefcase,
  Store
} from 'lucide-react';
import Logo from '../Logo';
import { SEO } from '../SEO';
import { Button } from '../ui/Button';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'wouter';
import { SupportFooter } from '../SupportFooter';

interface LandingPageProps {
  onGetStarted: () => void;
}

const painPoints = [
  {
    icon: HelpCircle,
    title: "Too Many Options",
    description: "Hundreds of tools all claiming to be \"the best\" — how do you choose?"
  },
  {
    icon: Layers,
    title: "Hidden Overlap",
    description: "You're paying for 3 tools that do the same thing and don't realize it."
  },
  {
    icon: RefreshCw,
    title: "Switching Costs",
    description: "Picking the wrong tool now means painful migrations later."
  },
  {
    icon: CreditCard,
    title: "Budget Anxiety",
    description: "Enterprise pricing hidden behind \"Contact Sales\" — will this break your budget?"
  }
];

const blueprintComponents = [
  {
    name: "Core Stack",
    description: "The essential tools you need right now",
    detail: "4 tools matched to your exact business stage and needs"
  },
  {
    name: "Growth Stack",
    description: "What to add as you scale",
    detail: "Know what to add later as your business evolves"
  },
  {
    name: "Avoid List",
    description: "Tools that aren't right for you",
    detail: "Save money and time by knowing what to skip"
  },
  {
    name: "Why It Fits",
    description: "Clear rationale for each recommendation",
    detail: "Understand exactly why each tool matches your situation"
  },
  {
    name: "Upgrade Path",
    description: "When and how to level up",
    detail: "Triggers that signal it's time to switch or expand"
  }
];

const steps = [
  {
    icon: ClipboardList,
    title: "Answer Quick Questions",
    description: "3 minutes about your business, budget, and goals"
  },
  {
    icon: Sparkles,
    title: "We Analyze Your Fit",
    description: "Your answers are matched against 100+ tools"
  },
  {
    icon: CheckCircle2,
    title: "Get Your Blueprint",
    description: "A complete software strategy, not just a list"
  }
];

const personas = [
  {
    icon: Rocket,
    title: "Founders & Startups",
    description: "Building your first tech stack from scratch",
    link: null
  },
  {
    icon: Building2,
    title: "Small Business Owners",
    description: "Ready to professionalize your operations",
    link: null
  },
  {
    icon: Briefcase,
    title: "Freelancers & Consultants",
    description: "Need tools that punch above their weight",
    link: null
  },
  {
    icon: Users,
    title: "Growing Teams",
    description: "Scaling from scrappy to structured",
    link: null
  },
  {
    icon: Store,
    title: "Service Businesses",
    description: "Trades, agencies, and local businesses going digital",
    link: "/for-trades"
  }
];

const faqItems = [
  {
    question: "What is the Business Software Blueprint?",
    answer: "A personalized software strategy that includes your recommended tools, what to avoid, and a clear upgrade path as your business grows."
  },
  {
    question: "How long does it take to get my Blueprint?",
    answer: "About 3 minutes to complete the questionnaire, then instant access to your full Blueprint."
  },
  {
    question: "What if the recommendations don't work for me?",
    answer: "We offer a satisfaction guarantee. If the Blueprint isn't useful, contact us for a refund."
  }
];

const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How to Get Your Business Software Blueprint",
  "description": "Get a personalized software strategy for your exact business in under 3 minutes",
  "totalTime": "PT3M",
  "estimatedCost": { "@type": "MonetaryAmount", "currency": "USD", "value": "29" },
  "step": [
    {
      "@type": "HowToStep",
      "position": 1,
      "name": "Answer Quick Questions",
      "text": "Spend about 90 seconds telling us about your business type, team size, goals, budget, and technical comfort level.",
      "url": "https://btoolme.com/questionnaire",
      "itemListElement": [
        { "@type": "HowToTip", "text": "Be honest about your budget — it helps us filter out tools you'll never actually use." }
      ]
    },
    {
      "@type": "HowToStep",
      "position": 2,
      "name": "We Analyze Your Fit",
      "text": "Our recommendation engine matches your profile against 100+ vetted business tools across 36 categories, filtering for your exact stage and needs.",
      "itemListElement": [
        { "@type": "HowToTip", "text": "No paid placements — every recommendation is based purely on business fit." }
      ]
    },
    {
      "@type": "HowToStep",
      "position": 3,
      "name": "Unlock Your Full Blueprint",
      "text": "Get instant access to your complete Business Software Blueprint: Core Stack, Growth Stack, Avoid List, and a plain-English rationale for each recommendation.",
      "url": "https://btoolme.com/unlock",
      "itemListElement": [
        { "@type": "HowToTip", "text": "Your Blueprint includes an Avoid List — tools that seem popular but are wrong for your stage." }
      ]
    }
  ]
};

export function LandingPage({ onGetStarted }: LandingPageProps) {
  const [_, navigate] = useLocation();

  const seoTitle = "btoolme | The Faster Way To Find Software";
  const seoDescription = "btoolme finds what's best for your business in minutes! Get a personalized software strategy. One-time $29. No subscriptions.";

  return (
    <div className="min-h-screen flex flex-col" id="main-content">
      <SEO
        title={seoTitle}
        description={seoDescription}
        faqItems={faqItems}
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(howToSchema)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-white">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-100/40 rounded-full blur-3xl" />
          <div className="absolute top-20 -left-20 w-60 h-60 bg-cyan-100/30 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-50/50 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
          <div className="text-center max-w-4xl mx-auto">
            <Logo className="mb-8 mx-auto" />

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight mb-6">
              The Faster Way To Find Software
            </h1>

            <p className="text-xl sm:text-2xl text-gray-600 mb-4 font-medium">
              btoolme finds what's best for your business in minutes!
            </p>

            <p className="text-sm text-gray-400 mb-10">
              Built for real businesses — not generic "best software" lists.
            </p>

            <div className="flex flex-col items-center gap-4">
              <Button
                onClick={() => navigate('/questionnaire')}
                className="group px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg sparkle-button w-full sm:w-auto"
                size="lg"
                aria-label="Build your personalized software blueprint"
              >
                Build My Blueprint
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>

              {/* Guarantee Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-full">
                <Shield className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-700">
                  100% Confidence Guaranteed
                </span>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap justify-center gap-6 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-500" />
                <span>3-minute questionnaire</span>
              </div>
              <div className="flex items-center gap-2">
                <Ban className="w-4 h-4 text-blue-500" />
                <span>No subscriptions</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Sound Familiar?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choosing business software shouldn't feel like a high-stakes gamble.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {painPoints.map((point, index) => {
              const Icon = point.icon;
              return (
                <div
                  key={index}
                  className="flex gap-4 p-5 rounded-xl bg-gray-50 border border-gray-100"
                >
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-gray-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{point.title}</h3>
                    <p className="text-sm text-gray-600">{point.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Blueprint Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-blue-600 font-semibold text-sm uppercase tracking-wide mb-3">
              What You Get
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Your Business Software Blueprint
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Not just recommendations — a complete strategy for your software stack.
            </p>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">Component</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">What It Is</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-900">What You Get</th>
                  </tr>
                </thead>
                <tbody>
                  {blueprintComponents.map((component, index) => (
                    <tr
                      key={index}
                      className={index !== blueprintComponents.length - 1 ? "border-b border-gray-100" : ""}
                    >
                      <td className="py-4 px-6">
                        <span className="font-medium text-gray-900">{component.name}</span>
                      </td>
                      <td className="py-4 px-6 text-gray-600">{component.description}</td>
                      <td className="py-4 px-6 text-gray-600">{component.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4">
            {blueprintComponents.map((component, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-5"
              >
                <h3 className="font-semibold text-gray-900 mb-2">{component.name}</h3>
                <p className="text-sm text-gray-600 mb-2">{component.description}</p>
                <p className="text-sm text-blue-600 font-medium">{component.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20 bg-white border-y border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              How It Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="relative flex flex-col items-center text-center">
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-[2px] bg-gradient-to-r from-blue-200 to-transparent" />
                  )}

                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-gray-900 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {index + 1}
                    </div>
                  </div>

                  <h3 className="mt-5 text-lg font-semibold text-gray-900">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-gray-500 text-sm">
                    {step.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Built For
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {personas.slice(0, 3).map((persona, index) => {
              const Icon = persona.icon;
              return (
                <div
                  key={index}
                  className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200"
                >
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{persona.title}</h3>
                    <p className="text-sm text-gray-600">{persona.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 max-w-2xl mx-auto lg:max-w-none lg:grid-cols-2 lg:px-24">
            {personas.slice(3).map((persona, index) => {
              const Icon = persona.icon;
              const CardContent = (
                <>
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{persona.title}</h3>
                    <p className="text-sm text-gray-600">{persona.description}</p>
                    {persona.link && (
                      <span className="text-xs text-blue-600 font-medium mt-1 inline-block">Learn more →</span>
                    )}
                  </div>
                </>
              );

              if (persona.link) {
                return (
                  <a
                    key={index}
                    href={persona.link}
                    className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all"
                  >
                    {CardContent}
                  </a>
                );
              }

              return (
                <div
                  key={index}
                  className="flex items-start gap-3 p-4 bg-white rounded-xl border border-gray-200"
                >
                  {CardContent}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Related Resources */}
      <section className="py-12 sm:py-16 bg-white border-y border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">Explore More Resources</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <a href="/tools" className="group p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500">
              <p className="font-semibold text-gray-900 group-hover:text-blue-700 text-sm mb-1">Browse All 100+ Tools</p>
              <p className="text-xs text-gray-500">Search and filter our full curated directory</p>
            </a>
            <a href="/tools-needed-to-start-a-business" className="group p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500">
              <p className="font-semibold text-gray-900 group-hover:text-blue-700 text-sm mb-1">Starting a Business?</p>
              <p className="text-xs text-gray-500">Essential tools to launch without overbuying</p>
            </a>
            <a href="/what-software-should-a-small-business-use" className="group p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500">
              <p className="font-semibold text-gray-900 group-hover:text-blue-700 text-sm mb-1">Small Business Guide</p>
              <p className="text-xs text-gray-500">How to choose software for your specific situation</p>
            </a>
            <a href="/for-trades" className="group p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500">
              <p className="font-semibold text-gray-900 group-hover:text-blue-700 text-sm mb-1">Trades & Field Service</p>
              <p className="text-xs text-gray-500">Software built for contractors & service businesses</p>
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Build Your Blueprint?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Stop researching. Start building with confidence.
          </p>

          <div className="flex flex-col items-center gap-4">
            <Button
              onClick={() => navigate('/questionnaire')}
              className="group px-6 sm:px-10 py-4 sm:py-5 text-base sm:text-lg bg-white text-gray-900 hover:bg-gray-100 font-semibold rounded-xl shadow-xl shadow-black/20 hover:shadow-2xl hover:shadow-black/30 transition-all duration-300 hover:scale-[1.02]"
              size="lg"
            >
              Build My Blueprint
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>

            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full">
              <Shield className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-medium text-white">
                100% Confidence Guaranteed
              </span>
            </div>
          </div>

          <p className="mt-8 text-sm text-gray-400">
            Questions? <a href="mailto:btoolmepro@gmail.com" className="text-gray-300 hover:text-white underline">Contact us</a> — we're happy to help.
          </p>
        </div>
      </section>

      <SupportFooter variant="dark" className="bg-gray-900" />
    </div>
  );
}
